import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ICommodityTypeFormResult } from '../../../models/commodity-type/interfaces';
import { RanchCreateCommodityTypeDialog } from '../dialogs/commodity-type-dialog';

@Component({
	moduleId: module.id,
	selector: 'ranch-create-commodity-types-step',
	templateUrl: 'step-commodity-types.html',
	styleUrls: ['../ranch-create.scss']
})

export class RanchCreateCommodityTypesStepComponent {
	@Input()
	public form: FormArray;

	@Input()
	public subscriptions$: Subject<boolean>;

	@Output()
	public nextStep: EventEmitter<number> = new EventEmitter<number>();

	@Output()
	public onBack: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	public get commodityTypeControls(): AbstractControl[] {
		if (!this.form) {
			return [];
		}

		return this.form.controls;
	}

	constructor(
		private _dialog: MatDialog,
		private _fb: FormBuilder
	) { }

	public back(): void {
		this.onBack.emit();
	}

	public cancel(): void {
		this.onCancel.emit();
	}

	public deleteCommodityType(index: number, event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}

		this.form.removeAt(index);
		this.form.markAsDirty();
	}

	public next(): void {
		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		if (!this.form.valid) {
			return;
		}

		this.nextStep.emit(2);
	}

	public openAddCommodityTypeDialog(): void {
		this._openCommodityTypeDialog((result: ICommodityTypeFormResult) => {
			if (!result) {
				return;
			}

			this.form.push(this._initializeForm(result));
			this.form.markAsDirty();
		});
	}

	public openEditCommodityTypeDialog(index: number, event?: MouseEvent): void {
		let commodityType: ICommodityTypeFormResult;

		if (event) {
			event.stopPropagation();
		}

		commodityType = this.form.at(index).value;

		this._openCommodityTypeDialog((result: ICommodityTypeFormResult) => {
			if (!result) {
				return;
			}

			this.form.setControl(index, this._initializeForm(result));
			this.form.markAsDirty();
		}, commodityType, index);
	}

	private _initializeForm(result: ICommodityTypeFormResult): FormGroup {
		return this._fb.group({
			CommodityTypeId: [result.CommodityTypeId, Validators.required],
			CommodityTypeName: [result.CommodityTypeName, Validators.required],
			CropTypeIds: [result.CropTypeIds, Validators.required]
		});
	}

	private _getSelectedCommodityTypeIds(excludeIndex?: number): number[] {
		return this.form.controls
			.filter((control, index) => excludeIndex === undefined || index !== excludeIndex)
			.map((control) => control.value as ICommodityTypeFormResult)
			.map((commodityType) => commodityType.CommodityTypeId);
	}

	private _openCommodityTypeDialog(
		onClose: (result: ICommodityTypeFormResult) => void,
		commodityType?: ICommodityTypeFormResult,
		index?: number
	): void {
		this._dialog.open(RanchCreateCommodityTypeDialog, {
			width: '500px',
			data: {
				commodityType: commodityType,
				selectedCommodityTypeIds: this._getSelectedCommodityTypeIds(index)
			}
		}).afterClosed().pipe(takeUntil(this.subscriptions$)).subscribe(onClose);
	}
}
